import dayjs from "dayjs";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { RsvpEventDetails } from "queries/rsvp.queries";
import React, { useEffect } from "react";
import { Pressable, Text, View } from "react-native";
import { rsvpTone, useRsvpColors } from "../theme";

interface Props {
  events: RsvpEventDetails[];
  modelValue: number | null;
  onChange?: (eventId: number) => void;
  // When set, that event's card is highlighted and badged as the player's RSVP.
  reservedEventId?: number | null;
  // Text shown in the reserved-event chip (e.g. "Your RSVP" or "Waitlist").
  reservedLabel?: string;
  // Renders the cards as a non-interactive list (used in the reserved state).
  readOnly?: boolean;
  label?: string;
  // Validation message; when set, the section is outlined in red and the
  // message is shown beneath the list.
  error?: string | null;
}

/**
 * Lists the events sharing a slug (same-named events, distinguished by date).
 * In the eligible state it's a selectable picker; in the reserved state it's a
 * read-only list with the reserved event highlighted.
 */
const RsvpEventPicker: React.FC<Props> = ({
  events,
  modelValue,
  onChange,
  reservedEventId,
  reservedLabel = "Your RSVP",
  readOnly = false,
  label = "Select an event",
  error,
}) => {
  const { data: tenantConfiguration } = useFetchTenantConfigurationQuery();
  const colors = useRsvpColors();

  useEffect(() => {
    if (!readOnly && events.length === 1 && modelValue !== events[0].eventId) {
      onChange?.(events[0].eventId);
    }
  }, [events, modelValue, onChange, readOnly]);

  if (events.length <= 1) return null;

  const highlightId = reservedEventId ?? modelValue;

  return (
    <View
      style={[
        { gap: 8, marginBottom: 24 },
        error
          ? {
              borderWidth: 1,
              borderColor: rsvpTone.danger.color,
              borderRadius: 12,
              padding: 12,
            }
          : null,
      ]}
      accessibilityRole={readOnly ? undefined : "radiogroup"}
      testID="rsvp-event-picker"
    >
      <Text
        style={{
          color: error ? rsvpTone.danger.color : colors.textSecondary,
          fontSize: 14,
          fontWeight: "500",
          marginBottom: 4,
        }}
      >
        {label}
      </Text>
      {events.map((event) => {
        const highlighted = highlightId === event.eventId;
        const reserved = reservedEventId === event.eventId;
        // A full event (capacity reached, no waitlist) cannot be picked
        const full = !reserved && event.rsvpFull;
        const selectable = !readOnly && !full;
        return (
          <Pressable
            key={event.eventId}
            accessibilityRole={readOnly ? undefined : "radio"}
            accessibilityState={
              readOnly ? undefined : { selected: highlighted, disabled: full }
            }
            onPress={selectable ? () => onChange?.(event.eventId) : undefined}
            disabled={!selectable}
            style={{
              minHeight: 56,
              borderRadius: 12,
              borderWidth: 1,
              padding: 16,
              backgroundColor: highlighted ? colors.accentTint : colors.cardBg,
              borderColor: highlighted ? colors.accent : colors.cardBorder,
              // Only dim unhighlighted rows when a highlighted row exists
              // (the reserved-state list); a plain read-only list stays full.
              // Full events are always dimmed.
              opacity:
                full || (readOnly && highlightId != null && !highlighted)
                  ? 0.6
                  : 1,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              {/* Events sharing a slug have the same name (shown once above
                  the list), so each tile carries only its date */}
              <Text
                style={{
                  color: colors.textPrimary,
                  fontSize: 16,
                  fontWeight: "600",
                  flexShrink: 1,
                }}
              >
                {dayjs(event.eventDate)
                  .tz(tenantConfiguration?.timezone)
                  .format("ddd, MMM D · h:mm A")}
              </Text>
              {reserved ? (
                <View
                  style={{
                    backgroundColor: colors.accent,
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 3,
                  }}
                >
                  <Text
                    style={{
                      color: colors.onAccent,
                      fontSize: 11,
                      fontWeight: "700",
                    }}
                  >
                    {reservedLabel}
                  </Text>
                </View>
              ) : full ? (
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: colors.cardBorder,
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 3,
                  }}
                >
                  <Text
                    style={{
                      color: colors.textMuted,
                      fontSize: 11,
                      fontWeight: "600",
                    }}
                  >
                    Full
                  </Text>
                </View>
              ) : event.waitlisting ? (
                <View
                  style={{
                    backgroundColor: colors.accentTint,
                    borderWidth: 1,
                    borderColor: colors.accent,
                    borderRadius: 999,
                    paddingHorizontal: 10,
                    paddingVertical: 3,
                  }}
                >
                  <Text
                    style={{
                      color: colors.accent,
                      fontSize: 11,
                      fontWeight: "600",
                    }}
                  >
                    Waitlist
                  </Text>
                </View>
              ) : null}
            </View>
          </Pressable>
        );
      })}
      {error ? (
        <Text
          accessibilityRole="alert"
          style={{
            color: rsvpTone.danger.color,
            fontSize: 13,
            fontWeight: "600",
            marginTop: 4,
          }}
          testID="rsvp-event-picker-error"
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
};

export default RsvpEventPicker;
